export interface SupabaseBrowserCredentials {
  url: string | undefined
  apiKey: string | undefined
  isProd: boolean
}

function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

/**
 * Секретный ключ в браузере — катастрофа: бандл раздаётся всем, а sb_secret_*
 * даёт права service_role в обход RLS. Падаем сразу и громко, иначе опечатка в
 * переменной окружения деплоя разошлась бы по всем клиентам молча.
 */
function assertNotSecretKey(apiKey: string | undefined): string | undefined {
  if (apiKey?.startsWith('sb_secret_')) {
    throw new Error(
      'В клиентские переменные Supabase попал СЕКРЕТНЫЙ ключ (sb_secret_...). ' +
      'Браузеру нужен publishable-ключ: секретный даёт права service_role в обход RLS.',
    )
  }
  return apiKey
}

export function getSupabaseCredentials(): SupabaseBrowserCredentials {
  const environment = String(import.meta.env.ENVIRONMENT || 'dev').toLowerCase()
  const isLocalDev = import.meta.env.DEV

  // Deployed builds get VITE_* values from the hosting workflow. Keep the legacy anon
  // variable as a rollback fallback until JWT-based API keys are disabled and verified.
  if (!isLocalDev && import.meta.env.VITE_SUPABASE_URL) {
    return {
      url: import.meta.env.VITE_SUPABASE_URL,
      apiKey: assertNotSecretKey(firstNonEmpty(
        import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        import.meta.env.VITE_SUPABASE_ANON_KEY,
      )),
      isProd: true,
    }
  }

  if (environment === 'prod') {
    return {
      url: import.meta.env.PROD_SUPABASE_URL,
      apiKey: assertNotSecretKey(firstNonEmpty(
        import.meta.env.PROD_SUPABASE_PUBLISHABLE_KEY,
        import.meta.env.PROD_SUPABASE_ANON_KEY,
      )),
      isProd: true,
    }
  }

  return {
    url: import.meta.env.DEV_SUPABASE_URL,
    apiKey: assertNotSecretKey(firstNonEmpty(
      import.meta.env.DEV_SUPABASE_PUBLISHABLE_KEY,
      import.meta.env.DEV_SUPABASE_ANON_KEY,
    )),
    isProd: false,
  }
}

/**
 * Оба заголовка обязательны.
 *
 * ⚠️ Замер PROD 11.09.2026: PostgREST берёт роль из `Authorization`, а `apikey`
 * лишь открывает проект. Запрос с одним `apikey` отвечает 200 и пустым массивом
 * (роль падает до anon, ошибки нет), запрос с одним `Authorization` — 401
 * «No API key found in request». Непрозрачный ключ в `Authorization` шлюз
 * принимает, поэтому вырезать его нельзя. Настоящий пользовательский JWT
 * вытесняет ключ проекта — так PostgREST видит авторизованную личность.
 */
export function createSupabaseRequestHeaders(
  apiKey: string,
  bearer?: string | null,
  initial?: HeadersInit,
): Headers {
  const headers = new Headers(initial)
  headers.set('apikey', apiKey)
  headers.set('Authorization', `Bearer ${bearer?.trim() || apiKey}`)
  return headers
}
